<template>
  <BaseModule
    v-bind="$props"
    class="contact-module"
    data-module="contact"
  >
    <div class="content">
      <header
        v-if="title || subtitle || text"
        class="base-title-fontsize"
      >
        <component
          :is="titleTag"
          v-if="title"
          class="tit tit-g"
        >
          {{ title }}
        </component>
        <h3
          v-if="subtitle"
          class="sub"
        >
          {{ subtitle }}
        </h3>

        <div
          v-if="text"
          class="txt"
          v-html="text"
        ></div>
      </header>

      <form
        class="form-base"
        novalidate
        @submit.prevent="send"
      >
        <div class="field-row">
          <div
            class="field field__vselect"
            :class="{ error: !isValidContactSubject }"
          >
            <v-select
              id="subject"
              v-model="contactSubject"
              class="vselect"
              label="subject"
              :options="subjects.filter(({ subject }) => subject)"
              :reduce="({ subject }) => subject"
              :clearable="false"
              :placeholder="$t('placeHolderSubject')"
              @search:blur="startValidate('subject')"
            >
              <div slot="no-options">Nenhum resultado encontrado</div>
            </v-select>
          </div>
          <div
            v-if="hasLetsbookIntegration && !useDefaultHotel"
            class="field field__vselect"
          >
            <v-select
              id="hotel-contact"
              v-model="contactHotel"
              class="vselect"
              label="Nome"
              :options="hotelsListOptions"
              :clearable="false"
              :reduce="(opt) => opt.Codigo"
            >
              <div slot="no-options">Nenhum resultado encontrado</div>
            </v-select>
          </div>
        </div>
        <div class="field-row">
          <div
            class="field"
            :class="{ error: !isValidContactName }"
          >
            <input
              :id="`name-${moduleId}`"
              v-model.trim="contactName"
              name="name"
              type="text"
              :placeholder="$t('placeHolderName')"
              @blur="startValidate('name')"
            />
          </div>
          <div
            class="field"
            :class="{ error: !isValidContactSurname }"
          >
            <input
              :id="`surname-${moduleId}`"
              v-model.trim="contactSurname"
              name="surname"
              type="text"
              :placeholder="$t('placeHolderLastName')"
              @blur="startValidate('surname')"
            />
          </div>
        </div>
        <div class="field-row">
          <div
            class="field"
            :class="{ error: !isValidContactEmail }"
          >
            <input
              :id="`email-${moduleId}`"
              v-model.trim="contactEmail"
              name="email"
              type="email"
              placeholder="E-mail*"
              @blur="startValidate('email')"
            />
          </div>
          <div
            class="field"
            :class="{ error: !isValidContactPhone }"
          >
            <input
              :id="`phone-${moduleId}`"
              v-model.trim="contactPhone"
              name="phone"
              type="tel"
              maxlength="16"
              :placeholder="$t('placeHolderPhone')"
              @blur="startValidate('phone')"
            />
          </div>
        </div>
        <div class="field-row">
          <div
            class="field"
            :class="{ error: !isValidContactMessage }"
          >
            <textarea
              :id="`message-${moduleId}`"
              v-model.trim="contactMessage"
              name="message"
              :placeholder="$t('placeHolderMessage')"
              @blur="startValidate('message')"
            ></textarea>
          </div>
        </div>
        <div class="optin">
          <input
            :id="optinId"
            v-model="contactOptin"
            type="checkbox"
          />
          <label
            ref="consentText"
            :for="optinId"
          >
            <span v-html="consentText"></span>
            <small v-html="consentTextSmall"></small>
          </label>
        </div>
        <div class="field buttons">
          <div
            v-if="wasSent || editing"
            class="message"
            :class="{
              message__success: wasSentWithSuccess,
              message__error: !wasSentWithSuccess,
            }"
          >
            <span
              v-if="wasSentWithSuccess"
              v-html="successMessage"
            ></span>
            <span v-else>
              {{ $t('errorMessage') }}
            </span>
          </div>
          <!-- popup exclusivo do Editor -->
          <div class="editor-form-disclaimer editor-form-disclaimer--right">
            <span class="slb-tooltip arrow-right-down">
              Os envios de formulários são desativados no preview. Para testar
              seu funcionamento, acesse sua página publicada.
            </span>
            <!-- botão desabilitado no Editor -->
            <button
              type="submit"
              class="cta"
              :disabled="true"
            >
              {{ submitButtonLabel }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </BaseModule>
</template>

<script>
import { mapGetters } from 'vuex';
import vSelect from 'vue-select';

import uniqueId from '@/helpers/uniqueId';
import getApiHotels from '@/mixins/getApiHotels';
import base from '@/mixins/base';
import BaseModule from './generic/BaseModule.vue';

export default {
  name: 'Contact',
  components: {
    BaseModule,
    'v-select': vSelect,
  },
  mixins: [getApiHotels, base],
  inheritAttrs: false,
  props: {
    ...BaseModule.props,
    text: {
      type: String,
      default: '',
    },
    subjects: {
      type: Array,
      default() {
        return [];
      },
    },
    successMessage: {
      type: String,
      default: '',
    },
    consentText: {
      type: String,
      default: 'Aceito receber comunicações e ofertas do hotel.',
    },
    consentTextSmall: {
      type: String,
      default:
        'Ao enviar, estará ciente de que seus dados serão tratados pelo hotel com a finalidade exclusiva de envio de ações de marketing.',
    },
    defaultSelectedHotel: {
      type: String,
      default: '',
    },
    useDefaultHotel: {
      type: Boolean,
      default: false,
    },
    backgroundColor: {
      type: String,
      default: '',
    },
    titleColor: {
      type: String,
      default: '#ffffff',
    },
    textColor: {
      type: String,
      default: '#ffffff',
    },
    useCrm: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      contactSubject: '',
      contactHotel: this.useDefaultHotel ? this.defaultSelectedHotel : '',
      contactName: '',
      contactSurname: '',
      contactEmail: '',
      contactPhone: '',
      contactMessage: '',
      contactOptin: false,
      optinId: null,
      isSending: false,
      validatingFields: [],
      wasSent: false,
      wasSentWithSuccess: true,
    };
  },
  computed: {
    hotelsListOptions() {
      return [
        {
          Nome: this.$t('placeHolderHotel'),
          Codigo: '',
        },
        ...this.hotels,
      ];
    },
    submitButtonLabel() {
      if (this.isSending) {
        return this.$t('sendingText');
      }

      return this.$t('submitButton');
    },
    contactEmails() {
      if (this.contactSubject === '') {
        return [];
      }

      const { emails } = this.subjects.find(
        (subject) => subject.subject === this.contactSubject,
      );

      return emails || [];
    },
    idsCRMNovasValid() {
      if (this.contactSubject === '') {
        return [];
      }

      // eslint-disable-next-line arrow-body-style
      const { crmIdsNewPeople } = this.subjects.find((subject) => {
        return subject.subject === this.contactSubject;
      });

      return crmIdsNewPeople || [];
    },
    idsCRMExistentesValid() {
      if (this.contactSubject === '') {
        return [];
      }

      // eslint-disable-next-line arrow-body-style
      const { crmIdsKnownPeople } = this.subjects.find((subject) => {
        return subject.subject === this.contactSubject;
      });

      return crmIdsKnownPeople || [];
    },
    editing() {
      return typeof this.$parent === 'undefined';
    },
    isValidContactSubject() {
      return (
        !this.validatingFields.includes('subject') || this.contactSubject !== ''
      );
    },
    isValidContactName() {
      return !this.validatingFields.includes('name') || this.contactName !== '';
    },
    isValidContactSurname() {
      return (
        !this.validatingFields.includes('surname') || this.contactSurname !== ''
      );
    },
    isValidContactEmail() {
      return (
        !this.validatingFields.includes('email') ||
        this.isValidEmail(this.contactEmail)
      );
    },
    isValidContactPhone() {
      // Phones must have at least 10 numbers
      return (
        !this.validatingFields.includes('phone') ||
        this.contactPhone.replace(/\D/g, '').length >= 10
      );
    },
    isValidContactMessage() {
      return (
        !this.validatingFields.includes('message') || this.contactMessage !== ''
      );
    },
    moduleId() {
      return this.instanceId.replace(/\D/g, '');
    },
    ...mapGetters(['settings', 'hasLetsbookIntegration', 'activePageId']),
  },
  mounted() {
    this.optinId = uniqueId();

    if (this.hasLetsbookIntegration) {
      this.getApiHotels({ getSorted: true });
    }
  },
  methods: {
    isValid() {
      this.startValidate('subject');
      this.startValidate('name');
      this.startValidate('surname');
      this.startValidate('email');
      this.startValidate('phone');
      this.startValidate('message');

      return (
        this.isValidContactSubject &&
        this.isValidContactName &&
        this.isValidContactSurname &&
        this.isValidContactEmail &&
        this.isValidContactPhone &&
        this.isValidContactMessage
      );
    },
    isValidEmail(email) {
      const emailPattern =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

      return emailPattern.test(email);
    },
    send() {
      this.wasSent = false;

      if (this.isValid()) {
        this.isSending = true;

        let hotelName = this.contactHotel;

        if (typeof this.contactHotel === 'number') {
          hotelName = this.hotels.find(
            (el) => el.Codigo === this.contactHotel,
          )?.Nome;
        }

        const body = {
          to: this.contactEmails,
          subject: `Novo contato de ${this.contactName} ${this.contactSurname} (${this.contactSubject})`,
          sendEmail: true,
          integrated: this.useCrm,
          id: this.activePageId,
          url: window.location.href,
          data: {
            hotel: {
              value: hotelName,
              label: 'Hotel',
            },
            name: {
              value: this.contactName,
              label: 'Nome',
            },
            surname: {
              value: this.contactSurname,
              label: 'Sobrenome',
            },
            email: {
              value: this.contactEmail,
              label: 'E-mail',
            },
            phone: {
              value: this.contactPhone,
              label: 'Telefone',
            },
            message: {
              value: this.contactMessage,
              label: 'Mensagem',
            },
            consentText: {
              value: this.$refs.consentText.textContent,
              label: 'Texto de consentimento',
            },
            acceptedConsent: {
              value: this.contactOptin,
              label: 'Aceitou o consentimento',
            },
          },
          crmData: {
            groups: this.idsCRMNovasValid.map((id) => ({
              Numgrupo: Number(id),
            })),
            alternativeGroups: this.idsCRMExistentesValid.map((id) => ({
              Numgrupo: Number(id),
            })),
          },
        };

        const { apiBaseUrl, customer } = this.$store.state;

        fetch(`${apiBaseUrl}contact/${customer}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
          },
          body: JSON.stringify(body),
        })
          .then((res) => {
            this.wasSentWithSuccess = res.status === 200;

            if (this.wasSentWithSuccess) {
              this.resetForm();
            }
          })
          .catch(() => {
            this.wasSentWithSuccess = false;
          })
          .finally(() => {
            this.isSending = false;
            this.wasSent = true;
          });
      }
    },
    resetForm() {
      this.contactSubject = '';
      this.contactHotel = '';
      this.contactName = '';
      this.contactSurname = '';
      this.contactEmail = '';
      this.contactPhone = '';
      this.contactMessage = '';
      this.contactOptin = false;
      this.validatingFields = [];
    },
    startValidate(field) {
      if (!this.validatingFields.includes(field)) {
        this.validatingFields.push(field);
      }
    },
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'text',
            fields: [
              {
                name: 'text',
                type: 'Html',
                label: 'Texto de apoio',
                wrapWithParagraph: false,
              },
              {
                name: 'successMessage',
                type: 'LongText',
                label: 'Mensagem de confirmação',
              },
              {
                name: 'consentText',
                type: 'Html',
                maxlength: 200,
                label: 'Texto de consentimento',
              },
              {
                name: 'consentTextSmall',
                type: 'Html',
                label: 'Nota de consentimento',
              },
            ],
          },
          {
            name: 'config',
            label: 'Configurações',
            position: 0,
            fields: [
              {
                name: 'useCrm',
                type: 'Checkbox',
                label: 'Usar integração com CRM',
                isEnabled({ crmToken }) {
                  return crmToken;
                },
              },
              {
                name: 'useDefaultHotel',
                type: 'Checkbox',
                label: 'Definir hotel para recebimento do formulário',
                isEnabled({ hasLetsbookIntegration }) {
                  return hasLetsbookIntegration;
                },
              },
              {
                name: 'defaultSelectedHotel',
                type: 'VueSelect',
                label:
                  'Selecione o hotel que irá receber os envios do formulário',
                isEnabled({ useDefaultHotel }) {
                  return useDefaultHotel;
                },
                options() {
                  const hotelsToOptions = (hotels) =>
                    hotels.map((hotel) => ({
                      label: hotel.Nome,
                      value: hotel.Nome,
                    }));

                  if (this.hotels.length > 0) {
                    return hotelsToOptions(this.hotels);
                  }

                  return new Promise((resolve) => {
                    const unwatch = this.$watch('hotels', (hotels) => {
                      resolve(hotelsToOptions(hotels));
                      unwatch();
                    });
                  });
                },
              },
            ],
          },
          {
            name: 'subjectsList',
            label: 'Lista de assuntos',
            fields: [
              {
                name: 'subjects',
                type: 'List',
                minItems: 1,
                validation: {
                  length: {
                    minimum: 1,
                    message: 'Adicione pelo menos um assunto',
                  },
                },
                itemLabel({ subject }) {
                  return subject || 'Assunto {index}'; // any {index} will be replaced by item's index (1-based)
                },
                addItemLabel: 'Adicionar assunto',
                fields: [
                  {
                    name: 'subject',
                    type: 'ShortText',
                    label: 'Assunto',
                    maxlength: 50,
                    validation: {
                      presence: {
                        allowEmpty: false,
                        message:
                          'Adicione o assunto para o recebimento dos e-mails',
                      },
                    },
                  },
                  {
                    name: 'emails',
                    type: 'Tags',
                    label: 'Destinatários',
                    helpText: `
                      Os destinatários precisam estar cadastrados na aba de Configurações.
                      <a href="/settings#email" target="_blank">
                      <b>Clique aqui para ver a lista completa</b></a>.
                    `,
                    whitelistFromCustomerSetting: 'emails',
                    pattern:
                      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i,
                    validation: {
                      presence: {
                        allowEmpty: false,
                        message:
                          'Adicione, no mínimo, um destinatário para o recebimento dos e-mails',
                      },
                    },
                  },
                  {
                    name: 'crmIdsNewPeople',
                    type: 'Tags',
                    label: 'IDs dos grupos do CRM - Pessoas novas',
                    helpText:
                      'Grupos de leads que se cadastraram pela primeira vez',
                    pattern: /^\d+$/,
                    isEnabled({ useCrm }) {
                      return useCrm;
                    },
                    validation: {
                      presence: {
                        allowEmpty: false,
                        message:
                          'Adicione, no mínimo, um grupo de CRM para pessoas novas',
                      },
                    },
                  },
                  {
                    name: 'crmIdsKnownPeople',
                    type: 'Tags',
                    label: 'IDs dos grupos do CRM - Pessoas existentes',
                    helpText: 'Grupos de leads que se cadastraram novamente',
                    pattern: /^\d+$/,
                    isEnabled({ useCrm }) {
                      return useCrm;
                    },
                    validation: {
                      presence: {
                        allowEmpty: false,
                        message:
                          'Adicione, no mínimo, um grupo de CRM para pessoas existentes',
                      },
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  i18n: {
    messages: {
      pt: {
        placeHolderName: 'Nome*',
        placeHolderSubject: 'Assunto*',
        placeHolderHotel: 'Hotel (opcional)',
        placeHolderLastName: 'Sobrenome*',
        placeHolderPhone: 'Telefone*',
        placeHolderMessage: 'Mensagem*',
        errorMessage: 'Ops! Algo errado aconteceu ao enviar seu contato.',
        submitButton: 'Enviar',
        sendingText: 'Enviando...',
      },
      en: {
        placeHolderName: 'Name*',
        placeHolderSubject: 'Subject*',
        placeHolderHotel: 'Hotel (optional)',
        placeHolderLastName: 'Last name*',
        placeHolderPhone: 'Phone*',
        placeHolderMessage: 'Message*',
        errorMessage:
          'Oops! Something wrong happened when sending your contact.',
        submitButton: 'Send',
        sendingText: 'Sending...',
      },
      es: {
        placeHolderName: 'Nombre*',
        placeHolderSubject: 'Sujeto*',
        placeHolderHotel: 'Hotel (opcional)',
        placeHolderLastName: 'Apellido*',
        placeHolderPhone: 'Teléfono*',
        placeHolderMessage: 'Mensaje*',
        errorMessage: '¡UPS! Ocurrió algo incorrecto al enviar su contacto.',
        submitButton: 'Entregar',
        sendingText: 'Enviando...',
      },
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep {
  .v-select {
    width: 100% !important;

    .vs__dropdown-toggle {
      border: none;
      padding: 0;

      .vs__selected-options {
        max-width: calc(100% - 23px);

        input {
          margin: 0;
          padding: 0;
        }
      }

      .vs__selected {
        width: calc(100% - 10px);
        display: block;
        margin: 0;
        padding: 0;
        color: $lb-medium-gray;

        text-align: left;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    .vs__dropdown-option {
      color: $lb-medium-gray;
      font-size: $size-normal;
      line-height: 1.2;
      white-space: unset;
      padding: 6px 20px;

      &--highlight {
        color: $lb-white;
      }
    }

    ::placeholder {
      color: $lb-medium-gray;
    }
  }
}

header {
  .txt {
    &::v-deep {
      strong {
        font-weight: $lb-bold !important;
      }

      a,
      a:visited {
        color: inherit;
        text-decoration: underline;
      }
    }
  }
}

section.contact-module {
  height: auto;
  overflow: hidden;
  padding: 0 20px;
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);
  background-color: var(--bg-color, var(--primary-color));

  .cta {
    margin-left: auto;
    height: 0%;
    background-color: var(--button-color, var(--secondary-color));
  }

  .form-base {
    margin: 0 auto;
    max-width: 800px;

    .field-row {
      display: flex;
    }

    .field {
      margin-bottom: 20px;
      width: 100%;

      &__vselect {
        min-width: calc(50% - 10px);
      }

      & + .field {
        margin-left: 20px;
      }

      &.buttons {
        margin-bottom: 0;
        width: 100%;
        margin-top: 1em;
        display: flex;
        justify-content: space-between;

        .message {
          border: 1px solid $lb-black;
          padding: 1em 2em;
          display: inline-block;
          text-align: left;

          &__success {
            background-color: #d9ffd9;
            border-color: green;
            border-radius: 5px;
            color: green;
            white-space: pre-line;
          }

          &__error {
            background-color: #fde6eb;
            border-color: crimson;
            border-radius: 5px;
            color: crimson;
          }
        }
      }

      &.error {
        input,
        #subject,
        textarea {
          border-color: crimson;
        }

        input[type='text']::placeholder,
        input[type='tel']::placeholder,
        input[type='email']::placeholder,
        textarea::placeholder,
        ::v-deep input::placeholder {
          color: crimson;
        }

        ::v-deep .vs__open-indicator {
          fill: crimson;
        }
      }

      input,
      textarea,
      .vselect {
        background: $lb-white;
        border: 2px solid transparent;
        border-radius: 5px;
        color: $lb-medium-gray;
        font-size: $size-normal;
        transition: 200ms;
        padding: 10px;
        width: 100%;
      }

      textarea {
        height: 150px;
        resize: vertical;
      }
    }

    .optin {
      clear: both;
      color: var(--text-color);
      text-align: left;

      label {
        font-size: $size-normal;
        display: inline;
      }

      input {
        float: left;
        margin: 0.25em 0.5em 0 0;
      }

      small {
        display: block;
        font-size: $size-smaller;
      }

      ::v-deep {
        a {
          color: inherit;
          text-decoration: underline;

          &:hover {
            opacity: 0.8;
          }
        }
      }
    }
  }
}

@include media-breakpoint-down(sm) {
  section.contact-module {
    padding: 0 20px;
    padding-top: var(--padding-top-mobile);
    padding-bottom: var(--padding-bottom-mobile);

    .form-base {
      .field-row {
        display: block;
      }

      .field {
        margin-left: 0 !important;
        margin-bottom: 10px;

        &.buttons {
          margin-top: 1em;
          flex-direction: column-reverse;

          button {
            border-radius: 0;
            width: 100%;
          }

          .message {
            display: block !important;
            margin-right: 0 !important;
            margin-top: 10px;
          }
        }
      }
    }
  }
}
</style>
